import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { OktaAuth, AuthState, UserClaims } from '@okta/okta-auth-js'
import config from '../config'

interface OktaAuthContextType {
  oktaAuth: OktaAuth | null
  authState: AuthState | null
  user: UserClaims | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | undefined>
}

const OktaAuthContext = createContext<OktaAuthContextType | undefined>(undefined)

interface OktaAuthProviderProps {
  children: ReactNode
}

export const OktaAuthProvider: React.FC<OktaAuthProviderProps> = ({ children }) => {
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null)
  const [authState, setAuthState] = useState<AuthState | null>(null)
  const [user, setUser] = useState<UserClaims | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    // Skip Okta initialization if authentication is disabled
    if (!config.okta.enabled) {
      console.log('Okta authentication is disabled')
      if (isMounted) {
        setIsLoading(false)
      }
      return
    }

    // Validate Okta configuration
    if (!config.okta.issuer || !config.okta.clientId) {
      console.error('Okta configuration is incomplete. Missing issuer or clientId.')
      if (isMounted) {
        setIsLoading(false)
      }
      return
    }

    const initializeOkta = async () => {
      try {
        const oktaAuthInstance = new OktaAuth({
          issuer: config.okta.issuer,
          clientId: config.okta.clientId,
          redirectUri: config.okta.redirectUri,
          postLogoutRedirectUri: config.okta.postLogoutRedirectUri,
          scopes: config.okta.scopes,
          pkce: true,
          devMode: config.app.environment === 'development',
          // Add additional configuration to handle first-time login issues
          transformErrorXHR: (xhr: any) => {
            // Handle CORS and network errors more gracefully
            if (xhr.status === 0) {
              console.warn('Network error during authentication - this may be temporary')
            }
            return xhr
          }
        })

        if (!isMounted) return

        setOktaAuth(oktaAuthInstance)

        // Set up auth state listener with better error handling
        const handleAuthStateChange = async (newAuthState: AuthState) => {
          if (!isMounted) return

          console.log('Auth state changed:', newAuthState)
          setAuthState(newAuthState)

          // Only set loading to false after we've processed the auth state
          if (newAuthState.isAuthenticated && newAuthState.idToken) {
            try {
              // Get user information with retry logic
              const userInfo = await oktaAuthInstance.getUser()
              if (isMounted) {
                console.log('User info:', userInfo)
                setUser(userInfo)
                setIsLoading(false)
              }
            } catch (error) {
              console.error('Error getting user info:', error)
              if (isMounted) {
                setUser(null)
                setIsLoading(false)
              }
            }
          } else {
            if (isMounted) {
              setUser(null)
              setIsLoading(false)
            }
          }
        }

        oktaAuthInstance.authStateManager.subscribe(handleAuthStateChange)

        // Start the auth state manager and wait for initial state
        await oktaAuthInstance.start()

        // Set a timeout to ensure loading state is cleared even if auth state doesn't change
        setTimeout(() => {
          if (isMounted && isLoading) {
            console.log('Timeout reached - clearing loading state')
            setIsLoading(false)
          }
        }, 5000)

      } catch (error) {
        console.error('Error initializing Okta Auth:', error)
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    initializeOkta()

    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [])

  const login = async (): Promise<void> => {
    if (!oktaAuth) {
      throw new Error('Okta Auth not initialized')
    }

    try {
      await oktaAuth.signInWithRedirect()
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  const logout = async (): Promise<void> => {
    if (!oktaAuth) {
      throw new Error('Okta Auth not initialized')
    }

    try {
      await oktaAuth.signOut()
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }

  const getAccessToken = async (): Promise<string | undefined> => {
    if (!oktaAuth || !authState?.isAuthenticated) {
      return undefined
    }

    try {
      const accessToken = oktaAuth.getAccessToken()

      // If token is expired or about to expire, try to renew it
      if (!accessToken || oktaAuth.tokenManager.hasExpired(accessToken)) {
        await oktaAuth.tokenManager.renew('accessToken')
        return oktaAuth.getAccessToken()
      }

      return accessToken
    } catch (error) {
      console.error('Error getting access token:', error)
      return undefined
    }
  }

  const contextValue: OktaAuthContextType = {
    oktaAuth,
    authState,
    user,
    isAuthenticated: config.okta.enabled ? (authState?.isAuthenticated ?? false) : true,
    isLoading,
    login,
    logout,
    getAccessToken
  }

  return (
    <OktaAuthContext.Provider value={contextValue}>
      {children}
    </OktaAuthContext.Provider>
  )
}

export const useOktaAuth = (): OktaAuthContextType => {
  const context = useContext(OktaAuthContext)
  if (context === undefined) {
    throw new Error('useOktaAuth must be used within an OktaAuthProvider')
  }
  return context
}

export default OktaAuthContext
